import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const fmtUsd = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
const POLL_MS = 2000;
const MAX_POLLS = 8;

export default function LessonsBookingSuccess() {
  const [state, setState] = useState({ status: "polling", booking: null, message: "Confirming your booking…" });
  const nav = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState({ status: "error", message: "Missing session_id" }); return; }

    let cancelled = false;
    let polls = 0;

    const poll = async () => {
      if (cancelled) return;
      polls += 1;
      try {
        const { data } = await api.get(`/lessons/bookings/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "success", booking: data.booking, message: "Lesson booked!" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", booking: data.booking, message: "Payment expired. Please try again." });
          return;
        }
        if (polls >= MAX_POLLS) {
          setState({ status: "error", booking: data.booking, message: "Still waiting on payment confirmation — check your email shortly." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (polls >= MAX_POLLS) { setState({ status: "error", message: "Could not check status." }); return; }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--ink-1)" }}>
      <div className="w-full max-w-md rounded-2xl ink-card p-8 text-center" data-testid="lessons-booking-success">
        <div className="mb-4 flex justify-center"><Logo size="sm" variant="lockup" dark /></div>

        {state.status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-cyan-400 mb-3" />
            <div className="font-display font-bold text-xl text-white">{state.message}</div>
          </>
        )}

        {state.status === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400 mb-3" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-emerald-400">Confirmed</div>
            <h1 className="font-display font-black text-3xl text-white tracking-tighter mt-1">{state.message}</h1>
            {state.booking && (
              <div className="mt-5 rounded-xl bg-white/[0.03] border border-white/10 p-4 text-left text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-white/50">Coach</span><span className="text-white font-bold">{state.booking.coach_name}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Lesson</span><span className="text-white">{state.booking.lesson_label}</span></div>
                <div className="flex justify-between"><span className="text-white/50">When</span><span className="text-white font-mono-num">{new Date(state.booking.slot_iso).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Amount</span><span className="text-white font-bold">{fmtUsd(state.booking.amount_cents)}</span></div>
              </div>
            )}
            <Button className="w-full mt-5 text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}
              onClick={() => nav("/portal/lessons")} data-testid="success-go-bookings">
              See my bookings
            </Button>
          </>
        )}

        {state.status === "error" && (
          <>
            <AlertCircle className="h-12 w-12 mx-auto text-amber-400 mb-3" />
            <div className="font-display font-bold text-xl text-white">{state.message}</div>
            <Button className="w-full mt-5" variant="outline" onClick={() => nav("/lessons")} data-testid="success-back-marketplace">
              Back to marketplace
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
